import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../api/client';

interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    roles: string[];
    permissions: string[];
}

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;

    login: (email: string, password: string) => Promise<void>;
    logout: () => void;
    hasPermission: (permission: string) => boolean;
    hasRole: (role: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            login: async (email: string, password: string) => {
                set({ isLoading: true, error: null });

                try {
                    const response = await api.post('/api/auth/login', { email, password });
                    const { accessToken, user } = response.data;

                    set({
                        user,
                        token: accessToken,
                        isAuthenticated: true,
                        isLoading: false,
                    });

                    // Set token for future requests
                    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
                } catch (error: any) {
                    set({
                        error: error.response?.data?.message || 'Login failed',
                        isLoading: false,
                    });
                    throw error;
                }
            },

            logout: () => {
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                });
                delete api.defaults.headers.common['Authorization'];
            },

            hasPermission: (permission: string) => {
                const { user } = get();
                if (!user) return false;

                // Wildcard permission (Owner role)
                if (user.permissions.includes('*:*')) return true;

                return user.permissions.includes(permission);
            },

            hasRole: (role: string) => {
                const { user } = get();
                if (!user) return false;
                return user.roles.includes(role);
            },
        }),
        {
            name: 'curecraft-auth',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated,
            }),
        }
    )
);

// Initialize auth header on app load
const savedState = JSON.parse(localStorage.getItem('curecraft-auth') || '{}');
if (savedState.state?.token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${savedState.state.token}`;
}
